// pages/dashboard/Transactions.jsx
// Shows all payment transactions
// Summary metrics and detailed list
// CSV export

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const Transactions = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchTransactions();
  }, [filter]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? '/dashboard/transactions?limit=50'
        : `/dashboard/transactions?status=${filter}&limit=50`;
      const response = await axios.get(url);
      if (response.data.success) {
        setTransactions(response.data.data.transactions);
        setSummary(response.data.data.summary);
      }
    } catch (err) {
      console.error('Transactions fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Company', 'Amount', 'Status', 'PayPal ID', 'Date'];
    const rows = transactions.map(t => [
      `${t.userId?.firstName} ${t.userId?.lastName}`,
      t.userId?.email,
      t.userId?.companyName || 'N/A',
      `$${t.amount}`,
      t.status,
      t.paypalTransactionId || 'N/A',
      new Date(t.createdAt).toLocaleDateString(),
    ]);

    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusBadge = (status) => {
    const map = {
      completed: { bg: '#E8F8EF', color: '#1E8449', label: 'Completed' },
      pending: { bg: '#FEF9E7', color: '#A04000', label: 'Pending' },
      failed: { bg: '#FADBD8', color: '#922B21', label: 'Failed' },
      refunded: { bg: '#EBF5FB', color: '#1A5276', label: 'Refunded' },
    };
    const b = map[status] || { bg: '#F4F6F9', color: '#7F8C8D', label: status };
    return <span style={{ backgroundColor: b.bg, color: b.color, padding: '3px 10px', borderRadius: '100px', fontSize: '12px', fontWeight: '500' }}>{b.label}</span>;
  };

  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋' },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳', active: true },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢' },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝' },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.adminName}>{admin?.name}</div>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Transactions</h1>
            <p style={s.subtitle}>All payment records</p>
          </div>
          <button style={s.btnExport} onClick={exportCSV}>
            Export CSV
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div style={s.summaryGrid}>
            {[
              { label: 'Total Revenue', value: `$${(summary.totalRevenue || 0).toFixed(2)}`, icon: '💰', color: '#27AE60' },
              { label: 'Total Transactions', value: summary.totalTransactions || 0, icon: '📊', color: '#1B9AAA' },
              { label: 'Average Transaction', value: `$${(summary.avgTransaction || 0).toFixed(2)}`, icon: '📈', color: '#0A2342' },
            ].map((m, i) => (
              <div key={i} style={s.summaryCard}>
                <div style={s.summaryIcon}>{m.icon}</div>
                <div style={{ ...s.summaryValue, color: m.color }}>{m.value}</div>
                <div style={s.summaryLabel}>{m.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={s.filters}>
          {['all', 'completed', 'pending', 'failed'].map(f => (
            <button
              key={f}
              style={{ ...s.filterBtn, ...(filter === f ? s.filterActive : {}) }}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div style={s.card}>
          {loading ? (
            <p style={s.loadingText}>Loading transactions...</p>
          ) : transactions.length === 0 ? (
            <div style={s.empty}>
              <div style={s.emptyIcon}>💳</div>
              <p style={s.emptyTitle}>No transactions yet</p>
              <p style={s.emptyText}>Transactions will appear here once users make payments.</p>
            </div>
          ) : (
            <div>
              <div style={s.tableHead}>
                <span>Name</span>
                <span>Email</span>
                <span>Amount</span>
                <span>Status</span>
                <span>PayPal ID</span>
                <span>Date</span>
              </div>
              {transactions.map(t => (
                <div key={t._id} style={s.tableRow}>
                  <span style={s.name}>{t.userId?.firstName} {t.userId?.lastName}</span>
                  <span style={s.cell}>{t.userId?.email}</span>
                  <span style={{ ...s.cell, fontWeight: '700', color: '#27AE60' }}>${t.amount}</span>
                  <span style={s.cell}>{getStatusBadge(t.status)}</span>
                  <span style={{ ...s.cell, fontSize: '11px', fontFamily: 'monospace' }}>
                    {t.paypalTransactionId ? t.paypalTransactionId.substring(0, 16) + '...' : 'N/A'}
                  </span>
                  <span style={s.cell}>{new Date(t.createdAt).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: 'var(--color-text-light)' },
  btnExport: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' },
  summaryCard: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '24px', boxShadow: 'var(--shadow-sm)' },
  summaryIcon: { fontSize: '28px', marginBottom: '8px' },
  summaryValue: { fontSize: '28px', fontWeight: '700', lineHeight: 1, marginBottom: '4px' },
  summaryLabel: { fontSize: '13px', color: 'var(--color-text-light)' },
  filters: { display: 'flex', gap: '8px', marginBottom: '20px' },
  filterBtn: { padding: '8px 16px', borderRadius: '100px', border: '1.5px solid var(--color-border)', backgroundColor: '#fff', color: 'var(--color-text-light)', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  filterActive: { border: '1.5px solid var(--color-teal)', backgroundColor: 'var(--color-info-bg)', color: 'var(--color-teal)' },
  card: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)', padding: '24px' },
  loadingText: { padding: '40px', textAlign: 'center', color: 'var(--color-text-light)' },
  empty: { padding: '60px 20px', textAlign: 'center' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: 'var(--color-text-light)' },
  tableHead: { display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 0.8fr 0.8fr 1.2fr 0.7fr', gap: '12px', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' },
  tableRow: { display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 0.8fr 0.8fr 1.2fr 0.7fr', gap: '12px', padding: '12px', borderRadius: '8px', backgroundColor: 'var(--color-off-white)', alignItems: 'center', marginBottom: '4px' },
  name: { fontSize: '14px', fontWeight: '600', color: 'var(--color-primary)' },
  cell: { fontSize: '13px', color: 'var(--color-text-light)' },
};

export default Transactions;
